import CustomSidebar from '../../components/Sidebar/CustomSidebar';
import { ChangeEvent, useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import ButtonGroup from '../../components/ButtonGroup/ButtonGroup';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Input from '../../components/Input/Input';
import { MultiDatePicker_Range } from '../../components/MyDatePicker/MultiDatePicker';
import { black, green, red } from '../../utils/style/colors';
import Textarea from '../../components/Textarea/Textarea';
import Button from '../../components/Button/Button';
import moment from 'moment-jalaali';
import {
  endOfDate,
  jMonthIndex,
  monthToTimestamp,
  smartDateString,
  startOfDate,
} from '../../utils/date';
import notify from '../../components/Notify/Notify';
import Collapsible from '../../components/Collapsible/Collapsible';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

interface ISmsPanelProps {
  path: string;
}

const Container = styled.div`
  width: 100dvw;
  height: 100dvh;
  padding: 1rem;
`;

export const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const Box = styled.div`
  width: 100%;
  margin: 0 auto;

  border: 1px solid ${black};
  border-radius: 0.5rem;
  padding: 0.5rem;
`;

export const CheckCreditsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
`;

export const CheckCreditsItem = styled.div`
  flex: 1;
  display: flex;
  gap: 1rem;
  flex-direction: column;
  justify-content: center;
`;

const ChargeContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
`;

export const AccountBalanceContainer = styled.div`
  margin: 0 auto;
  width: 100%;
`;

export const LineBadge = styled.div`
  height: 3rem;
  border: 1px solid ${black};
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
`;

export const TextColor = styled.div`
  color: ${(props) => props.color};
`;

export interface IClientStats {
  sourceType: string;
  requestCount: number;
  totalMessageLength: number;
  totalCost: number;
}

// interface ISummary {
//   hospitalName: string;
//   sentMessageCount: number;
//   totalMessageLength: number;
// }

export interface IClientItem {
  id: string | number;
  name: string;
  title: string | null;
  allowBalance: number;
  balanceCheck: boolean | undefined;
  balanceIRR: number;
  notificationLine: string | null;
  hidden: boolean;
}

export interface IHistory {
  date: number;
  amountIRR: number;
  clientId: string;
  description: string;
  id: string;
}

const DOMAIN = 'https://sms.rastech.ir' + '/smsserver';
// const DOMAIN = 'http://localserver:8088/smsserver';

interface IInitialState {
  selectedTitle: string | null;
  notificationLine: string | null;
  checkCredits: boolean;
  minCredit: number;
  currentBalance: number;
  hidden: boolean;
}

interface IAction {
  type: 'update' | 'reset';
  payload: Partial<IInitialState>;
}

const userInfoInitialState: IInitialState = {
  selectedTitle: null,
  notificationLine: null,
  checkCredits: false,
  minCredit: 0,
  currentBalance: 0,
  hidden: false,
};

const userInfoReducer = (state: IInitialState, action: IAction) => {
  switch (action.type) {
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
    case 'reset':
      return userInfoInitialState;
    default:
      return state;
  }
};

const SmsPanel: React.FC<ISmsPanelProps> = ({ path: _ }) => {
  const { t } = useTranslation('general');
  const user = useSelector((state: RootState) => state.thisUser);
  const ACCESS_TOKEN = user.access_token;
  const dateOptions = {
    currentMonth: `${t('currentMonth')}`,
    prevMonth: `${t('prevMonth')}`,
    currentSeason: `${t('currentSeason')}`,
    prevSeason: `${t('prevSeason')}`,
    custom: `${t('customDateSetting')}`,
  };

  const [selectedItem, setSelectedItem] = useState<IClientItem | null>(null);
  const [listItems, setListItems] = useState<Array<IClientItem>>([]);
  const [showHidden, setShowHidden] = useState<boolean>(true);

  const filteredListItems = showHidden
    ? listItems
    : listItems.filter((item) => !item.hidden);

  const [userInfoState, dispatch] = useReducer(
    userInfoReducer,
    userInfoInitialState
  );

  const [selectedDateOption, setSelectedDateOptions] = useState<
    keyof typeof dateOptions | ''
  >('');
  const [customDate, setCustomDate] = useState<{
    startDate: number;
    dueDate: number;
  }>();
  const [chargingAmount, setChargingAmount] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [clientStats, setClientStats] = useState<IClientStats[]>();
  const [history, setHistory] = useState<IHistory[]>();
  // const [summary, setSummary] = useState<ISummary[]>();

  // loading can be '', 'charging', 'saveMinCredit'
  const [loading, setLoading] = useState<null | 'charging' | 'saveMinCredit'>(
    null
  );

  const handleChangeSelectedItem = (selectedItem: IClientItem) => {
    if (listItems?.length) {
      setSelectedItem(listItems.find((item) => item.id === selectedItem.id)!);
    }
  };

  const validatePhoneNumber = () => {
    return (
      userInfoState.notificationLine?.length &&
      /^(\+98|0098|098|0)?9\d{9}$/.test(userInfoState.notificationLine)
    );
  };

  const handleSaveUserInfo = async () => {
    setLoading('saveMinCredit');

    const isValidPhoneNumber =
      (userInfoState.notificationLine || '').length > 0
        ? validatePhoneNumber()
        : true;

    if (!isValidPhoneNumber) {
      notify(t('invalidPhoneNumber'), 'error');
      setLoading(null);
      return;
    }

    try {
      const res = await axios.post(
        `${DOMAIN}/api/client/updateClientInfo`,
        {
          ...selectedItem,
          notificationLine: userInfoState.notificationLine,
          title: userInfoState.selectedTitle,
          balanceCheck: userInfoState.checkCredits,
          allowBalance: !userInfoState.checkCredits
            ? 0
            : userInfoState.minCredit,
          balanceIRR: userInfoState.currentBalance,
          hidden: userInfoState.hidden,
        },
        {
          headers: {
            Authorization: ACCESS_TOKEN,
          },
        }
      );

      setListItems((prevState) => {
        return prevState.map((item) => {
          if (item.id === selectedItem!.id) {
            return {
              ...item,
              title: res.data.title,
              notificationLine: res.data.notificationLine,
              allowBalance: res.data.allowBalance,
              balanceCheck: res.data.balanceCheck,
              hidden: res.data.hidden,
            };
          }
          return item;
        });
      });
      dispatch({
        type: 'update',
        payload: {
          minCredit: res.data.allowBalance,
          checkCredits: res.data.balanceCheck,
          hidden: res.data.hidden,
        },
      });
      setLoading(null);
      notify(t('savedSuccessfully'));
    } catch (err) {
      notify(t('savedFailed'), 'error');
      setLoading(null);
    }
  };

  const handleAddCredit = async () => {
    setLoading('charging');
    if (description !== '') {
      const res = await axios.post(
        `${DOMAIN}/api/client/addCredit`,
        {},
        {
          headers: { Authorization: ACCESS_TOKEN },
          params: {
            clientName: selectedItem!.name,
            amountIRR: chargingAmount,
            description: description,
          },
        }
      );

      // handle reset form
      setDescription('');
      setChargingAmount(0);

      dispatch({
        type: 'update',
        payload: {
          currentBalance:
            Number(userInfoState.currentBalance) + res.data.amountIRR,
        },
      });

      handleGetHistory().then(() => {
        notify(t('chargedSuccessfully'));
        setLoading(null);
      });
    } else {
      notify(t('emptyDes'), 'error');
      setLoading(null);
    }
  };

  const handleGetSummary = async () => {
    // const res = await axios.get(`${DOMAIN}/api/sms/summary`, {
    //   params: {
    //     startDate: customDate?.startDate,
    //     endDate: customDate?.dueDate,
    //   },
    //   headers: {
    //     Authorization: ACCESS_TOKEN,
    //   },
    // });
  };

  const handleGetStatistics = async () => {
    const res = await axios.get(`${DOMAIN}/api/sms/clientStats`, {
      params: {
        clientName: selectedItem!.name,
        startDate: customDate?.startDate,
        endDate: customDate?.dueDate,
      },
      headers: {
        Authorization: ACCESS_TOKEN,
      },
    });

    setClientStats(res.data);
  };

  const handleGetHistory = async () => {
    const res = await axios.get(`${DOMAIN}/api/client/getAddCreditHistory`, {
      params: { clientName: selectedItem!.name },
      headers: {
        Authorization: ACCESS_TOKEN,
      },
    });

    setHistory(res.data);
  };

  useEffect(() => {
    const getAllClients = async () => {
      const res = await axios.get(`${DOMAIN}/api/client/getAll`, {
        headers: {
          Authorization: ACCESS_TOKEN,
        },
      });
      setListItems(res.data);

      if (selectedItem) {
        setSelectedItem(
          res.data.find((item: any) => item.id === selectedItem!.id)
        );
      }
    };

    getAllClients();
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem) {
      dispatch({
        type: 'update',
        payload: {
          selectedTitle: selectedItem.title ?? selectedItem.name,
          notificationLine: selectedItem.notificationLine,
          currentBalance: selectedItem.balanceIRR,
          checkCredits: selectedItem.balanceCheck as boolean,
          minCredit: selectedItem.allowBalance,
          hidden: selectedItem.hidden,
        },
      });
      setSelectedDateOptions('currentMonth');

      handleGetHistory();
      handleGetStatistics();
    }
  }, [selectedItem]);

  useEffect(() => {
    const currentDate = new Date();
    const currentMonthIndex = jMonthIndex(
      Number(moment(currentDate).format('jYYYY')),
      Number(moment(currentDate).format('jM'))
    );

    if (selectedDateOption !== 'custom') {
      setCustomDate({ startDate: 0, dueDate: 0 });
    }

    if (selectedDateOption === 'currentMonth') {
      const currentMonthTimestamp = new Date(
        monthToTimestamp(currentMonthIndex)
      );
      const startOfCurrentMonth = startOfDate(
        currentMonthTimestamp,
        'month'
      ).getTime();
      const endOfCurrentMonth = endOfDate(
        currentMonthTimestamp,
        'month'
      ).getTime();

      setCustomDate({
        startDate: startOfCurrentMonth,
        dueDate: endOfCurrentMonth,
      });
    } else if (selectedDateOption === 'prevMonth') {
      const prevMonthTimestamp = new Date(
        monthToTimestamp(currentMonthIndex - 1 < 0 ? 11 : currentMonthIndex - 1)
      );
      const startOfPrevMonth = startOfDate(
        prevMonthTimestamp,
        'month'
      ).getTime();
      const endOfPrevMonth = endOfDate(prevMonthTimestamp, 'month').getTime();
      setCustomDate({
        startDate: startOfPrevMonth,
        dueDate: endOfPrevMonth,
      });
    } else if (selectedDateOption === 'currentSeason') {
      const currentMonthTimestamp = new Date(
        monthToTimestamp(currentMonthIndex)
      );
      const startOfCurrentSeason = startOfDate(
        currentMonthTimestamp,
        'season'
      ).getTime();
      const endOfCurrentSeason = endOfDate(
        currentMonthTimestamp,
        'season'
      ).getTime();
      setCustomDate({
        startDate: startOfCurrentSeason,
        dueDate: endOfCurrentSeason,
      });
    } else if (selectedDateOption === 'prevSeason') {
      const prevMonthSeasonTimestamp = new Date(
        monthToTimestamp(
          currentMonthIndex - 3 < 0
            ? 11 + (currentMonthIndex - 3)
            : currentMonthIndex - 3
        )
      );
      const startOfPrevSeason = startOfDate(
        prevMonthSeasonTimestamp,
        'season'
      ).getTime();
      const endOfPrevSeason = endOfDate(
        prevMonthSeasonTimestamp,
        'season'
      ).getTime();
      setCustomDate({
        startDate: startOfPrevSeason,
        dueDate: endOfPrevSeason,
      });
    }
  }, [selectedDateOption]);

  useEffect(() => {
    if (selectedItem && (customDate?.startDate || customDate?.dueDate)) {
      handleGetSummary();
      handleGetStatistics();
      handleGetHistory();
    }
  }, [customDate]);

  return (
    <Container>
      <CustomSidebar
        height={'100%'}
        width={'15dvw'}
        sidebarListItems={filteredListItems}
        onChangeItem={handleChangeSelectedItem}
        menuHeader={
          <div className="flex gap-1 items-center">
            <div>{t('allClients') as string}</div>
            <div>
              <Input
                id="show-hidden-checkbox"
                label={t('showHidden') as string}
                type={'checkbox'}
                checked={showHidden}
                onChange={(event) => setShowHidden(event.target.checked)}
              />
            </div>
          </div>
        }
        selectedItem={selectedItem}
      >
        {selectedItem ? (
          <>
            <AccountBalanceContainer>
              <LineBadge>
                <div className="font-bold">{t('currentBalance')} : </div>
                <div className="flex gap-2 items-center">
                  <TextColor
                    color={userInfoState.currentBalance >= 0 ? green : red}
                  >
                    {userInfoState.currentBalance.toLocaleString()}
                  </TextColor>
                  <span>{t('IRR') as string}</span>
                </div>
              </LineBadge>
            </AccountBalanceContainer>
            <Box>
              <CheckCreditsContainer>
                <CheckCreditsItem>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <Input
                      id={`hidden-${selectedItem.id}`}
                      type="checkbox"
                      checked={userInfoState.hidden}
                      label={t('setAsHidden')}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        dispatch({
                          type: 'update',
                          payload: { hidden: event.target.checked },
                        });
                      }}
                    />
                    <Input
                      id={`checkCredits-${selectedItem.id}`}
                      type="checkbox"
                      checked={userInfoState.checkCredits}
                      label={t('checkCredits')}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        dispatch({
                          type: 'update',
                          payload: { checkCredits: event.target.checked },
                        });
                      }}
                    />
                  </div>
                  {userInfoState.checkCredits && (
                    <Input
                      type="number"
                      id="credit"
                      label={t('minCredit')}
                      wrapperStyle={{ flex: 1 }}
                      value={userInfoState.minCredit}
                      style={{ textAlign: 'start' }}
                      onChange={(e) => {
                        dispatch({
                          type: 'update',
                          payload: { minCredit: Number(e.target.value) },
                        });
                      }}
                    />
                  )}
                  <Input
                    id={`clinentInfoTitle-${selectedItem.id}`}
                    type="text"
                    label={t('title')}
                    value={userInfoState.selectedTitle as string}
                    onChange={(e) =>
                      dispatch({
                        type: 'update',
                        payload: { selectedTitle: e.target.value },
                      })
                    }
                  />
                  <Input
                    id={`notificationLine-${selectedItem.id}`}
                    type="text"
                    label={t('notificationLine') as string}
                    value={(userInfoState.notificationLine ?? '') as string}
                    onChange={(e) =>
                      dispatch({
                        type: 'update',
                        payload: { notificationLine: e.target.value },
                      })
                    }
                  />
                  <div className="flex-1 flex justify-end">
                    <Button
                      type={'button'}
                      onClick={handleSaveUserInfo}
                      color={'black-outline'}
                      loading={loading === 'saveMinCredit'}
                    >
                      {t('save')}
                    </Button>
                  </div>
                </CheckCreditsItem>
              </CheckCreditsContainer>
            </Box>

            <Collapsible
              style={{ overflow: 'visible' }}
              header={<h4>{t('chargingAccount')}</h4>}
            >
              <ChargeContainer>
                <Input
                  type="number"
                  id="chargingAmount"
                  label={`${t('chargeAmount')} (${t('IRR') as string})`}
                  wrapperStyle={{ flex: 1 }}
                  style={{ textAlign: 'start' }}
                  value={chargingAmount}
                  onChange={(e) => {
                    setChargingAmount(Number(e.target.value));
                  }}
                />
                <Textarea
                  label={t('description')}
                  id="description"
                  placeholder={t('description')}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                >
                  {description}
                </Textarea>
                <div className={'flex justify-end'}>
                  <Button
                    type={'button'}
                    color={'black-outline'}
                    onClick={handleAddCredit}
                    loading={loading === 'charging'}
                  >
                    {t('charge') as string}
                  </Button>
                </div>
              </ChargeContainer>
            </Collapsible>

            <Box>
              <DateContainer>
                <ButtonGroup
                  items={dateOptions}
                  selected={selectedDateOption}
                  onSelect={(itemKey) => {
                    if (selectedDateOption) {
                      setCustomDate(undefined);
                    }
                    setSelectedDateOptions(itemKey as keyof typeof dateOptions);
                  }}
                  style={{ width: '100%', margin: '0 auto' }}
                />
                {selectedDateOption === 'custom' && (
                  <Box>
                    <MultiDatePicker_Range
                      isEditable={true}
                      hasTime={false}
                      label={t('customTime')}
                      values={{
                        startDate: customDate?.startDate as number,
                        dueDate: customDate?.dueDate as number,
                      }}
                      setDate={(values) => {
                        const startDate = values.startDate;
                        const dueDate = values.dueDate;
                        setCustomDate({
                          startDate: startDate as number,
                          dueDate: dueDate as number,
                        });
                      }}
                    />
                  </Box>
                )}
              </DateContainer>

              <div className="my-5">
                <h4 className="font-bold py-2">{t('smsStats')}</h4>
                <table>
                  <thead>
                    <tr>
                      <th>{t('topic') as string}</th>
                      <th>{t('smsNumber') as string}</th>
                      <th>{t('avgSmsLength') as string}</th>
                      <th>{t('sumCost') as string}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientStats?.map((stat, ind) => (
                      <tr key={`${stat.sourceType}-${ind}`}>
                        <td>{stat.sourceType}</td>
                        <td>{stat.requestCount}</td>
                        <td>{stat.totalMessageLength / stat.requestCount}</td>
                        <td>{stat.totalCost.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Box>

            <Box>
              <h4 className="font-bold py-2">{t('chargeHistory')}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t('date') as string}</th>
                    <th>
                      {t('chargeAmount') as string}({t('IRR') as string})
                    </th>
                    <th>{t('description') as string}</th>
                  </tr>
                </thead>
                <tbody>
                  {history?.map((item) => {
                    return (
                      <tr key={item.id}>
                        <td>
                          {smartDateString(
                            new Date(item.date),
                            true,
                            true,
                            true
                          )}
                        </td>
                        <td>{item.amountIRR.toLocaleString()}</td>
                        <td>{item.description}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </>
        ) : (
          <></>
        )}
      </CustomSidebar>
    </Container>
  );
};

export default SmsPanel;
